import {utils} from '@wallaby-ui/core';
import {cloneDeep, findIndex, forOwn} from 'lodash';
import {AnyAction} from 'redux';
import {LOCATION_CHANGE} from 'redux-first-history';

import * as at from './filtering_action_types';
import {Filter} from 'src/js/lib/filtering';
import {
  AutocompleteResult,
  AutocompleteOption,
} from 'src/js/model/autocomplete_result';
import {
  FilterSchema,
  FiltersConfig,
  FILTER_DEFAULT_VALUE,
} from 'src/js/model/filters_config';
import {HierarchyChildren} from 'src/js/model/hierarchy_children';
import {UserFilters} from 'src/js/model/user_filters';

const defaultFilterData = {
  isOpened: false,
};

const defaultState = {
  hierarchyLoadingCounters: [],
  schema: new FilterSchema(),
  filtersConfig: new FiltersConfig(),
  filtersData: {},
  selectedFilters: [],
  openedFilters: {},
  autocompleteOptions: [],
  autocompleteValues: {},
  hierarchyChildren: [],
  lastFocusedFilterId: null,
  locationState: {
    locationParsed: false,
    reload: false,
  },
  userFiltersLoading: {},
  userFilters: {},
};

function updateFilter(state, filter) {
  const itemIdx = Filter.getFilterSelectedIdx(state.selectedFilters, filter);
  let newState;
  if (itemIdx === -1) {
    // add filter
    newState = {
      ...state,
      selectedFilters: [...state.selectedFilters, filter],
    };
  } else {
    // update filter
    newState = {
      ...state,
      selectedFilters: utils.stateUtils.updateItemInArray(
        state.selectedFilters,
        {value: filter.value},
        itemIdx
      ),
    };
  }
  return {
    ...newState,
    openedFilters:
      filter.value != null && state.openedFilters[filter.property] == null
        ? {...state.openedFilters, [filter.property]: true}
        : state.openedFilters,
  };
}

function toggleOpenedFilter(state, filterName) {
  return {
    ...state,
    openedFilters: {
      ...state.openedFilters,
      [filterName]: !state.openedFilters[filterName],
    },
  };
}

function removeAndSetEmptyFilter(state, filter) {
  const itemIdx = Filter.getFilterSelectedIdx(state.selectedFilters, filter);
  if (itemIdx !== -1) {
    return {
      ...state,
      selectedFilters: utils.stateUtils.updateItemInArray(
        state.selectedFilters,
        {value: null},
        itemIdx
      ),
    };
  }
  return state;
}

function setFiltersDataProperty(state, filterId, property, value) {
  return {
    ...state,
    filtersData: {
      ...state.filtersData,
      [filterId]: {
        ...state.filtersData[filterId],
        [property]: value,
      },
    },
  };
}

function reducer(state = defaultState, action: AnyAction) {
  switch (action.type) {
    case LOCATION_CHANGE: {
      const {action: locationAction, query} = action.payload;
      if (
        state.locationState.locationParsed &&
        (locationAction === 'POP' || query.reload === 'true')
      ) {
        return {
          ...state,
          locationState: {
            ...state.locationState,
            reload: true,
            locationParsed: false,
          },
        };
      }
      break;
    }
    case at.LOCATION_PARSED: {
      return {
        ...state,
        ...(state.locationState.locationParsed !== action.payload
          ? {
              locationState: {
                ...state.locationState,
                locationParsed: action.payload,
              },
            }
          : {}),
      };
    }
    case at.RESET_RELOAD: {
      return {
        ...state,
        ...(state.locationState.reload !== action.payload
          ? {
              locationState: {
                ...state.locationState,
                reload: action.payload,
              },
            }
          : {}),
      };
    }
    case at.AUTOCOMPLETE_FETCH_SUCCESS: {
      const {params, response} = action.payload;
      return {
        ...state,
        autocompleteOptions: {
          ...state.autocompleteOptions,
          [params.type]: new AutocompleteResult(response),
        },
      };
    }
    case at.AUTOCOMPLETE_LABELS_FETCH_SUCCESS: {
      const {params, response} = action.payload;
      return {
        ...state,
        autocompleteValues: {
          ...state.autocompleteValues,
          [params.type]: new AutocompleteResult(response),
        },
      };
    }
    case at.AUTOCOMPLETE_SET_VALUES: {
      const {hierarchyType, values} = action.payload;
      return {
        ...state,
        autocompleteValues: {
          ...state.autocompleteValues,
          [hierarchyType]:
            AutocompleteResult.instantiateFromArrayOfValues(values),
        },
      };
    }
    case at.CHANGE_FILTER_NODE_PROPERTY: {
      const {id, property, value} = action.payload;
      return setFiltersDataProperty(state, id, property, value);
    }
    case at.HIERARCHY_CHILDREN_FETCH: {
      const {params} = action.payload;
      return {
        ...state,
        hierarchyLoadingCounters: {
          ...state.hierarchyLoadingCounters,
          [params.id]: (state.hierarchyLoadingCounters[params.id] || 0) + 1,
        },
      };
    }
    case at.HIERARCHY_CHILDREN_FETCH_FAILURE: {
      const {params} = action.payload;
      return {
        ...state,
        hierarchyLoadingCounters: {
          ...state.hierarchyLoadingCounters,
          [params.id]: Math.max(
            (state.hierarchyLoadingCounters[params.id] || 0) - 1,
            0
          ),
        },
      };
    }
    case at.HIERARCHY_CHILDREN_FETCH_SUCCESS: {
      const {params, response} = action.payload;
      const hierarchyNodeId = AutocompleteOption.getHierarchyNodeId(
        params.type,
        params.id
      );

      return {
        ...state,
        hierarchyChildren: {
          ...state.hierarchyChildren,
          [hierarchyNodeId]: new HierarchyChildren(response),
        },
        hierarchyLoadingCounters: {
          ...state.hierarchyLoadingCounters,
          [params.id]: Math.max(
            (state.hierarchyLoadingCounters[params.id] || 0) - 1,
            0
          ),
        },
      };
    }
    case at.FOCUS_FILTER: {
      return {
        ...state,
        lastFocusedFilterId: action.payload,
      };
    }
    case at.UPDATE_FILTER: {
      return updateFilter(state, action.payload);
    }
    case at.TOGGLE_OPENED: {
      return toggleOpenedFilter(state, action.payload);
    }
    case at.REMOVE_FILTER: {
      return removeAndSetEmptyFilter(state, action.payload);
    }
    case at.REMOVE_ALL_FILTERS: {
      return {
        ...state,
        selectedFilters: defaultState.selectedFilters,
        openedFilters: defaultState.openedFilters,
      };
    }
    case at.SCHEMA_RANGE_BOUNDS_FETCH_SUCCESS: {
      const {params, response} = action.payload;
      const itemIdx = findIndex(
        state.schema.filters,
        (filter) => filter.getName() === params.field
      );

      const filter = state.schema.filters[itemIdx];

      return {
        ...state,
        schema: utils.stateUtils.updateComplexObject(state.schema, {
          filters: utils.stateUtils.updateItemInArray(
            state.schema.filters,
            {rawData: {...filter.rawData, ...response}},
            itemIdx
          ),
        }),
      };
    }
    case at.FILTER_DEFAULT_VALUE_FETCH_SUCCESS: {
      const {filterId, response} = action.payload;
      // set default value
      return setFiltersDataProperty(
        state,
        filterId,
        FILTER_DEFAULT_VALUE,
        response
      );
    }
    case at.SET_FILTERS_CONFIG: {
      const {schema, filtersConfig, reload} = action.payload;
      if (reload || (schema && !state.schema.isLoaded())) {
        // init filters data
        const filtersData = {};
        forOwn(schema.getFilters(), (filter) => {
          filtersData[filter.getId()] = cloneDeep(defaultFilterData);
        });
        return {...state, filtersConfig, schema, filtersData};
      }
      break;
    }
    case at.USER_FILTERS_SAVE:
    case at.USER_FILTERS_SET_DEFAULT:
    case at.USER_FILTERS_REMOVE:
    case at.USER_FILTERS_FETCH: {
      const {type} = action.payload;
      return {
        ...state,
        userFiltersLoading: {
          ...state.userFiltersLoading,
          [type]: (state.userFiltersLoading[type] || 0) + 1,
        },
      };
    }
    case at.USER_FILTERS_SAVE_FAILURE:
    case at.USER_FILTERS_SET_DEFAULT_FAILURE:
    case at.USER_FILTERS_REMOVE_FAILURE:
    case at.USER_FILTERS_FETCH_FAILURE: {
      const {type} = action.payload;
      return {
        ...state,
        userFiltersLoading: {
          ...state.userFiltersLoading,
          [type]: Math.max((state.userFiltersLoading[type] || 0) - 1, 0),
        },
      };
    }
    case at.USER_FILTERS_FETCH_SUCCESS: {
      const {type, response} = action.payload;

      return {
        ...state,
        userFilters: {
          ...state.userFilters,
          [type]: new UserFilters(response),
        },
        userFiltersLoading: {
          ...state.userFiltersLoading,
          [type]: Math.max((state.userFiltersLoading[type] || 0) - 1, 0),
        },
      };
    }
    case at.USER_FILTERS_SAVE_SUCCESS:
    case at.USER_FILTERS_SET_DEFAULT_SUCCESS:
    case at.USER_FILTERS_REMOVE_SUCCESS: {
      const {type} = action.payload;

      return {
        ...state,
        userFiltersLoading: {
          ...state.userFiltersLoading,
          [type]: Math.max((state.userFiltersLoading[type] || 0) - 1, 0),
        },
      };
    }
  }
  return state;
}

export default reducer;
